// ===========================================
// Onboarding API Functions
// Student Onboarding System
// ===========================================

import { apiClient, publicClient } from '../services/api';
import type { ClassListing } from '../components/public/onboarding/ClassCatalog';

// ===========================================
// TYPES
// ===========================================

export interface OnboardingProgress {
  id: string;
  schoolId: string;
  studentId: string | null;
  familyId: string | null;
  registrationData: Record<string, unknown> | null;
  completedSteps: string[];
  currentStep: string;
  status: OnboardingStatus;
  freeTrialClassesAssigned: number;
  freeTrialClassesUsed: number;
  freeTrialScope: 'PER_STUDENT' | 'PER_FAMILY' | null;
  approvalStatus: string;
  paymentStatus: string;
  rejectionReason: string | null;
  termsAccepted: boolean;
  termsAcceptedAt: string | null;
  termsVersionAccepted: string | null;
  photoConsentGiven: boolean;
  photoConsentAcceptedAt: string | null;
  customFieldResponses: Record<string, unknown> | null;
  studentAgeAtRegistration: number | null;
  isAdultStudent: boolean;
  abandonedAt: string | null;
  startedAt: string;
  completedAt: string | null;
  lastActivityAt: string;
}

export type OnboardingStatus =
  | 'IN_PROGRESS'
  | 'AWAITING_APPROVAL'
  | 'AWAITING_PAYMENT'
  | 'TRIAL_ACTIVE'
  | 'COMPLETED'
  | 'REJECTED'
  | 'ABANDONED';

export interface StartOnboardingInput {
  studentFirstName: string;
  studentLastName: string;
  studentDateOfBirth: string;
  parentFirstName?: string;
  parentLastName?: string;
  parentEmail?: string;
  parentPhone?: string;
  studentEmail?: string;
  studentPhone?: string;
}

export interface StepCompletionData {
  [key: string]: unknown;
}

export interface OnboardingStepsInfo {
  steps: string[];
  completedSteps: string[];
  currentStep: string;
}

export interface OnboardingCounts {
  total: number;
  inProgress: number;
  awaitingApproval: number;
  awaitingPayment: number;
  trialActive: number;
  completed: number;
  rejected: number;
  abandoned: number;
}

interface ApiResponse<T> {
  status: string;
  data: T;
  message?: string;
}

interface PaginatedResponse<T> {
  status: string;
  data: T[];
  pagination: { total: number; page: number; limit: number };
}

// ===========================================
// PUBLIC API (No auth required)
// ===========================================

export async function startOnboarding(
  schoolSlug: string,
  data: StartOnboardingInput
): Promise<OnboardingProgress> {
  const response = await publicClient.post<ApiResponse<OnboardingProgress>>(
    `/public/onboarding/${schoolSlug}/start`,
    data
  );
  return response.data;
}

export async function getOnboardingProgress(
  schoolSlug: string,
  progressId: string
): Promise<OnboardingProgress> {
  const response = await publicClient.get<ApiResponse<OnboardingProgress>>(
    `/public/onboarding/${schoolSlug}/${progressId}`
  );
  return response.data;
}

export async function getOnboardingSteps(
  schoolSlug: string,
  progressId: string
): Promise<OnboardingStepsInfo> {
  const response = await publicClient.get<ApiResponse<OnboardingStepsInfo>>(
    `/public/onboarding/${schoolSlug}/${progressId}/steps`
  );
  return response.data;
}

export async function completeOnboardingStep(
  schoolSlug: string,
  progressId: string,
  step: string,
  data: StepCompletionData
): Promise<OnboardingProgress> {
  const response = await publicClient.post<ApiResponse<OnboardingProgress>>(
    `/public/onboarding/${schoolSlug}/${progressId}/step/${step}`,
    data
  );
  return response.data;
}

export async function addAnotherStudent(
  schoolSlug: string,
  progressId: string,
  data: { studentFirstName: string; studentLastName: string; studentDateOfBirth: string }
): Promise<OnboardingProgress> {
  const response = await publicClient.post<ApiResponse<OnboardingProgress>>(
    `/public/onboarding/${schoolSlug}/${progressId}/add-student`,
    data
  );
  return response.data;
}

// Raw shape returned by GET /api/public/schools/:slug/lessons
interface PublicLessonRaw {
  id: string;
  name: string;
  teacher: string;
  instrument: string;
  level: string;
  dayOfWeek: string;
  startTime: string;
  endTime: string;
  currentEnrollment: number;
  maxCapacity: number;
}

export async function getPublicLessons(
  schoolSlug: string,
  filters?: { instrumentId?: string; dayOfWeek?: string }
): Promise<ClassListing[]> {
  const params = new URLSearchParams();
  if (filters?.instrumentId) params.set('instrumentId', filters.instrumentId);
  if (filters?.dayOfWeek) params.set('dayOfWeek', filters.dayOfWeek);

  const query = params.toString() ? `?${params.toString()}` : '';
  const response = await publicClient.get<PublicLessonRaw[]>(
    `/public/schools/${schoolSlug}/lessons${query}`
  );

  // Map backend field names to the ClassListing interface used by ClassCatalog
  // publicClient.get already unwraps axios .data, so response is PublicLessonRaw[]
  return response.map((raw: PublicLessonRaw) => ({
    id: raw.id,
    name: raw.name,
    teacherName: raw.teacher,
    instrument: raw.instrument,
    level: raw.level,
    dayOfWeek: raw.dayOfWeek,
    startTime: raw.startTime,
    endTime: raw.endTime,
    enrolledCount: raw.currentEnrollment,
    capacity: raw.maxCapacity,
  }));
}

// ===========================================
// ADMIN API (Auth required)
// ===========================================

export async function listOnboardingAdmin(
  filters?: {
    status?: OnboardingStatus;
    search?: string;
    page?: number;
    limit?: number;
  }
): Promise<PaginatedResponse<OnboardingProgress>> {
  const params = new URLSearchParams();
  if (filters?.status) params.set('status', filters.status);
  if (filters?.search) params.set('search', filters.search);
  if (filters?.page) params.set('page', filters.page.toString());
  if (filters?.limit) params.set('limit', filters.limit.toString());

  return apiClient.get<PaginatedResponse<OnboardingProgress>>(
    `/admin/onboarding?${params.toString()}`
  );
}

export async function getOnboardingCountsAdmin(): Promise<OnboardingCounts> {
  const response = await apiClient.get<ApiResponse<OnboardingCounts>>(
    '/admin/onboarding/counts'
  );
  return response.data;
}

export async function getOnboardingAdmin(id: string): Promise<OnboardingProgress> {
  const response = await apiClient.get<ApiResponse<OnboardingProgress>>(
    `/admin/onboarding/${id}`
  );
  return response.data;
}

export async function approveOnboardingAdmin(id: string): Promise<OnboardingProgress> {
  const response = await apiClient.post<ApiResponse<OnboardingProgress>>(
    `/admin/onboarding/${id}/approve`
  );
  return response.data;
}

export async function rejectOnboardingAdmin(
  id: string,
  reason: string
): Promise<OnboardingProgress> {
  const response = await apiClient.post<ApiResponse<OnboardingProgress>>(
    `/admin/onboarding/${id}/reject`,
    { reason }
  );
  return response.data;
}

export async function reopenOnboardingAdmin(id: string): Promise<OnboardingProgress> {
  const response = await apiClient.post<ApiResponse<OnboardingProgress>>(
    `/admin/onboarding/${id}/reopen`
  );
  return response.data;
}
